import React, { useState } from 'react';
import { Modal, message } from 'antd';
import CategoryTree from './CategoryTree';
import type { Note } from '../types/note';
import { useUpdateNote } from '../hooks/useNoteMutations';
import {ROOT_LEVEL} from "../utils/constants";

interface MoveNoteModalProps {
  isVisible: boolean;
  note: Note;
  onCancel: () => void;
  onSuccess: (updatedNote: Note, selectedCategoryId: number) => void;
}

export default function MoveNoteModal({ isVisible, note, onCancel, onSuccess }: MoveNoteModalProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(note.category_id || ROOT_LEVEL.id);
  const [loading, setLoading] = useState(false);

  const updateNoteMutation = useUpdateNote();

  const handleMove = async () => {
    try {
      setLoading(true);
      const updatedNote = await updateNoteMutation.mutateAsync({
        id: note.id,
        note: {
          content: note.content,
          category_id: selectedCategoryId
        }
      });
      message.success('Note moved successfully');
      onSuccess(updatedNote, selectedCategoryId);
    } catch (error) {
      console.error('Failed to move note:', error);
      message.error('Failed to move note');
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
          onClick={onCancel}
          aria-hidden="true"
        />

        <div 
          className="relative z-50 inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium" id="modal-headline">
              Move Note
            </h3>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onCancel}
              aria-label="Close"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-[#0b1d51] mb-2">Select a category to move this note to:</p>
            <div className="min-h-[200px] max-h-96 overflow-y-auto border border-[#048ba8] rounded-lg p-3">
              <CategoryTree
                isMoveNoteModal={true}
                onSelectCategory={(categoryId: number, categoryBreadcrumb: string) => setSelectedCategoryId(categoryId)}
                showActions={false}
                selectedCategoryId={selectedCategoryId}
              />
            </div>
            
            <div className="p-3 bg-[#f4d35e] bg-opacity-20 rounded-lg">
              <p className="text-sm text-[#0b1d51]">
                <strong>Note:</strong> Moving this note will change its category assignment. 
                You can always move it back or to another category later.
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-[#849469] hover:text-[#849469]/90 focus:outline-none"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white bg-[#048ba8] hover:bg-[#048ba8]/90 rounded-2xl focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleMove}
              disabled={loading || !selectedCategoryId}
            >
              {loading ? 'Moving...' : 'Move'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
